import type {
  PlacementMode,
  PlacementPose,
  RuntimeAdapter,
  RuntimeSnapshot,
  RuntimeStopReason,
  RuntimeSupport,
} from "@/runtime/types";
import { WEBXR_FIXED_PLACEMENT } from "@/runtime/types";

interface XrStoreFacade {
  enterAR(): Promise<XRSession | undefined>;
  getState(): { session?: XRSession | null };
}

interface WebXRRuntimeOptions {
  surfaceTimeoutMs?: number;
}

export const WEBXR_SURFACE_TIMEOUT_MS = 7_000;

export class WebXRRuntime implements RuntimeAdapter {
  readonly mode = "webxr" as const;
  private readonly listeners = new Set<(snapshot: RuntimeSnapshot) => void>();
  private session: XRSession | null = null;
  private sessionToken = 0;
  private detachSessionListeners: (() => void) | null = null;
  private surfaceTimer: ReturnType<typeof setTimeout> | null = null;
  private placementBeforePause: PlacementPose | null = null;
  private snapshot: RuntimeSnapshot = {
    mode: "webxr",
    phase: "idle",
    placement: null,
    placementMode: "surface",
    error: null,
  };

  constructor(
    private readonly store: XrStoreFacade,
    private readonly options: WebXRRuntimeOptions = {},
  ) {}

  async probe(): Promise<RuntimeSupport> {
    return typeof window !== "undefined" && window.isSecureContext
      ? { supported: true }
      : { supported: false, reason: "secure-context-required" };
  }

  async start(): Promise<void> {
    const token = ++this.sessionToken;
    this.clearSurfaceTimer();
    this.update({ phase: "requesting-permission", error: null, placement: null });
    const session = await this.store.enterAR();
    if (!session) {
      this.update({ phase: "error", error: "The browser did not start an AR session." });
      throw new Error("The browser did not start an AR session.");
    }
    if (token !== this.sessionToken) {
      await session.end().catch(() => undefined);
      return;
    }

    this.session = session;
    this.attachSessionListeners(session);

    const placementMode: PlacementMode = session.enabledFeatures?.includes("hit-test") ? "surface" : "fixed";
    if (placementMode === "fixed") {
      this.update({ placementMode, placement: WEBXR_FIXED_PLACEMENT, phase: "placed", error: null });
      return;
    }
    this.update({ placementMode, placement: null, phase: "searching-surface", error: null });
    this.scheduleFixedPlacement(token);
  }

  async stop(reason: RuntimeStopReason): Promise<void> {
    void reason;
    ++this.sessionToken;
    this.clearSurfaceTimer();
    this.detachSessionListeners?.();
    this.detachSessionListeners = null;
    this.placementBeforePause = null;
    const session = this.session ?? this.store.getState().session;
    this.session = null;
    if (session) await session.end().catch(() => undefined);
    this.update({ phase: "stopped", placement: null, error: null });
  }

  async reposition(): Promise<void> {
    if (this.snapshot.placementMode === "fixed") {
      this.update({ placement: WEBXR_FIXED_PLACEMENT, phase: "placed" });
      return;
    }
    this.update({ placement: null, phase: "searching-surface" });
    this.scheduleFixedPlacement(this.sessionToken);
  }

  acceptPlacement(pose: PlacementPose): void {
    if (!this.session || this.snapshot.placementMode !== "surface") return;
    this.clearSurfaceTimer();
    this.update({ placement: pose, phase: "placed" });
  }

  /**
   * Wireframe fallback C. The session is still alive but the visitor has
   * covered the camera, backgrounded the tab, or walked somewhere the tracker
   * cannot relocalise. Hold the placement so `resume` can restore it.
   */
  markTrackingLost(): void {
    if (!this.session || this.snapshot.phase === "paused") return;
    this.clearSurfaceTimer();
    this.placementBeforePause = this.snapshot.placement;
    this.update({ phase: "paused" });
  }

  resume(): void {
    if (!this.session || this.snapshot.phase !== "paused") return;
    const restored = this.placementBeforePause;
    this.placementBeforePause = null;
    if (restored) {
      this.update({ phase: "placed", placement: restored });
      return;
    }
    this.update({ phase: "searching-surface", placement: null });
    this.scheduleFixedPlacement(this.sessionToken);
  }

  getSnapshot(): RuntimeSnapshot {
    return this.snapshot;
  }

  subscribe(listener: (snapshot: RuntimeSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  private attachSessionListeners(session: XRSession): void {
    const onEnd = () => {
      if (this.session !== session) return;
      this.clearSurfaceTimer();
      this.detachSessionListeners?.();
      this.detachSessionListeners = null;
      this.session = null;
      this.placementBeforePause = null;
      this.update({ phase: "stopped", placement: null, error: null });
    };
    // "visible-blurred" is the browser telling us the session lost the
    // foreground (system UI, permission sheet, app switch). Treat it the same
    // as losing tracking: the scene is no longer trustworthy but recoverable.
    const onVisibility = () => {
      if (this.session !== session) return;
      if (session.visibilityState === "visible") this.resume();
      else this.markTrackingLost();
    };
    session.addEventListener("end", onEnd, { once: true });
    session.addEventListener("visibilitychange", onVisibility);
    this.detachSessionListeners = () => {
      session.removeEventListener("end", onEnd);
      session.removeEventListener("visibilitychange", onVisibility);
    };
  }

  private scheduleFixedPlacement(token: number): void {
    this.clearSurfaceTimer();
    this.surfaceTimer = globalThis.setTimeout(() => {
      this.surfaceTimer = null;
      if (token !== this.sessionToken || !this.session || this.snapshot.phase !== "searching-surface") return;
      // A missing hit-test result must never become a dead end: drop to a
      // fixed pose in front of the viewer instead of waiting forever.
      this.update({
        placementMode: "fixed",
        placement: WEBXR_FIXED_PLACEMENT,
        phase: "placed",
        error: null,
      });
    }, this.options.surfaceTimeoutMs ?? WEBXR_SURFACE_TIMEOUT_MS);
  }

  private clearSurfaceTimer(): void {
    if (this.surfaceTimer === null) return;
    globalThis.clearTimeout(this.surfaceTimer);
    this.surfaceTimer = null;
  }

  private update(patch: Partial<RuntimeSnapshot>): void {
    const next = { ...this.snapshot, ...patch };
    if (JSON.stringify(next) === JSON.stringify(this.snapshot)) return;
    this.snapshot = next;
    for (const listener of this.listeners) listener(next);
  }
}
